// ©2024 Austin App House. All rights reserved.
//
// Dependency-free Module Federation loader for the CRA (Webpack 4) host.
//
// We deliberately do NOT use @module-federation/runtime here: its modern ESM /
// `isomorphic-rslog` build does not bundle cleanly under Webpack 4 and pulls in
// Node-only code. Instead we consume the remote the "classic" way:
//   1. inject the remoteEntry.js <script> (it sets `window.<remote>`),
//   2. initialize the container's share scope,
//   3. ask the container for an exposed module.
//
// The remote bundles its own React, so we share nothing (empty scope) and the
// host's React 16 never conflicts with the remote's React 18.

const NEXTJS_REMOTE_ENTRY =
  process.env.REACT_APP_NEXTJS_REMOTE_ENTRY ||
  'http://localhost:3010/_next/static/chunks/remoteEntry.js';

const REMOTE_GLOBAL = 'nextjsRemote';

const scriptPromises = {};

function loadScript(url) {
  if (scriptPromises[url]) return scriptPromises[url];

  scriptPromises[url] = new Promise((resolve, reject) => {
    const el = document.createElement('script');
    el.src = url;
    el.type = 'text/javascript';
    el.async = true;
    el.onload = () => resolve();
    el.onerror = () => {
      delete scriptPromises[url];
      reject(new Error(`Failed to load remote entry: ${url}`));
    };
    document.head.appendChild(el);
  });

  return scriptPromises[url];
}

let containerInitPromise = null;

async function getContainer() {
  await loadScript(NEXTJS_REMOTE_ENTRY);

  const container = window[REMOTE_GLOBAL];
  if (!container) {
    throw new Error(
      `Remote container "${REMOTE_GLOBAL}" was not found on window after loading ${NEXTJS_REMOTE_ENTRY}.`,
    );
  }

  if (!containerInitPromise) {
    // Share nothing from the host; the remote is self-contained. Some MF
    // runtimes throw if init() runs twice, so we guard + swallow that case.
    containerInitPromise = Promise.resolve()
      .then(() => container.init({}))
      .catch((err) => {
        if (!/already|initialized/i.test(String(err && err.message))) {
          throw err;
        }
      });
  }
  await containerInitPromise;

  return container;
}

/**
 * Load an exposed module from the Next.js remote.
 *
 * @param {string} moduleId e.g. 'nextjsRemote/mount'
 * @returns {Promise<any>} the module's default export (or the namespace)
 */
export async function loadRemoteModule(moduleId) {
  // 'nextjsRemote/mount' -> exposed key './mount'
  const exposedKey = `./${moduleId.split('/').slice(1).join('/')}`;

  const container = await getContainer();
  const factory = await container.get(exposedKey);
  const mod = factory();

  return mod && mod.default ? mod.default : mod;
}

export default loadRemoteModule;
